import { IDealsYearMeasures } from '@/bundles/Deals/types/interfaces/IDealsYearMeasures';
import { IDealsYearMeasureSplit } from '@/bundles/Deals/types/interfaces/IDealsYearMeasureSplit';
import { IDealsYearAggregate } from '@/bundles/Deals/types/interfaces/IDealsYearAggregate';
import { IDealsYearMonth } from '@/bundles/Deals/types/interfaces/IDealsYearMonth';
import { DealPaymentStatusEnum } from '@/bundles/Deals/types/enums/DealPaymentStatusEnum';

// `consideration` is a flat number (no payment split); the commission measures carry
// a { total, paid, due, overdue, booked } split. Methods below branch on that — the
// 0 / [] / {} returns are the consideration (no-split) case.
export default class DealsYearAggregateModel implements IDealsYearAggregate {
  year: number;
  deal_count: number;
  measures: IDealsYearMeasures;
  months: IDealsYearMonth[];

  constructor (payload: IDealsYearAggregate) {
    this.year = payload.year;
    this.deal_count = payload.deal_count;
    this.measures = payload.measures;
    this.months = payload.months;
  }

  private sortedMonths (): IDealsYearMonth[] {
    return this.months.slice().sort((first, second) => first.month - second.month);
  }

  monthlyDealCounts (): number[] {
    return this.sortedMonths().map(month => month.deal_count);
  }

  measureTotal (measure: keyof IDealsYearMeasures): number {
    const val = this.measures[measure];
    return typeof val === 'number' ? val : val.total;
  }

  hasStatusSplit (measure: keyof IDealsYearMeasures): boolean {
    return typeof this.measures[measure] !== 'number';
  }

  measureBooked (measure: keyof IDealsYearMeasures): number {
    const val = this.measures[measure];
    return typeof val === 'number' ? 0 : val.booked;
  }

  monthlyBooked (measure: keyof IDealsYearMeasures): number[] {
    if (!this.hasStatusSplit(measure)) return [];
    return this.sortedMonths().map(m => (m.measures[measure] as IDealsYearMeasureSplit).booked);
  }

  // Feeds the metric-sensitive bar and the GROSS axis label.
  monthlyMeasureTotals (measure: keyof IDealsYearMeasures): number[] {
    return this.sortedMonths().map(month => {
      const val = month.measures[measure];
      return typeof val === 'number' ? val : val.total;
    });
  }

  monthlySeriesByStatus (measure: keyof IDealsYearMeasures): Partial<Record<DealPaymentStatusEnum, number[]>> {
    if (!this.hasStatusSplit(measure)) return {};

    const months = this.sortedMonths();

    return {
      [DealPaymentStatusEnum.paid]: months.map(m => (m.measures[measure] as IDealsYearMeasureSplit).paid),
      [DealPaymentStatusEnum.due]: months.map(m => (m.measures[measure] as IDealsYearMeasureSplit).due),
      [DealPaymentStatusEnum.overdue]: months.map(m => (m.measures[measure] as IDealsYearMeasureSplit).overdue),
    };
  }

  yearStatusScaled (measure: keyof IDealsYearMeasures): Partial<Record<DealPaymentStatusEnum, number>> {
    const val = this.measures[measure];

    if (typeof val === 'number') return {};

    return {
      [DealPaymentStatusEnum.paid]: val.paid,
      [DealPaymentStatusEnum.due]: val.due,
    };
  }
}
